/**
    * @description      : Enhanced ACD AI Widget with better colors and modern UI
    * @author           : USER
    * @group            : 
    * @created          : 05/09/2025 - 00:54:54
    * 
    * MODIFICATION LOG
    * - Version         : 2.1.0
    * - Date            : 14/09/2025
    * - Author          : Assistant
    * - Modification    : Fixed authentication, name display, autofocus, and spacing
**/
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';

const AcdAiWidget = () => {
  const { user, isSignedIn, isLoaded } = useUser(); // Enhanced auth destructuring
  const [isOpen, setIsOpen] = useState(false);
  const [isFullPage, setIsFullPage] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [busy, setBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasInitializedMessages, setHasInitializedMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const suggestions = [
    "What programs do you offer?",
    "Help me analyze this debate topic",
    "Create debate topics for my age group",
    "How can I win my next competition?",
    "Teach me argument strategies",
    "What's the best way to refute arguments?"
  ];

  // Enhanced user name extraction - Updated for Clerk
  const getUserDisplayName = () => {
    if (!user || !isSignedIn) return null;

    // Priority order for name extraction from Clerk user
    const possibleNames = [
      user.firstName,
      user.fullName,
      user.username
    ].filter(Boolean);

    return possibleNames.length > 0 ? possibleNames[0] : null;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, typingText]);

  // Enhanced autofocus with proper timing
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure the element is fully rendered
      const focusTimeout = setTimeout(() => {
        inputRef.current.focus();
      }, 100);
      
      return () => clearTimeout(focusTimeout);
    }
  }, [isOpen, isFullPage]);

  // Initialize messages with proper user context and prevent duplicates
  useEffect(() => {
    if (isLoaded && !hasInitializedMessages) {
      const displayName = getUserDisplayName();
      const welcomeMessage = {
        type: 'ai',
        content: `Hello${displayName ? ` ${displayName}` : ''}! I'm ACD AI, your guide to African Child Debaters Champions. How can I assist you today?`,
        timestamp: Date.now()
      };

      setMessages([welcomeMessage]);
      setHasInitializedMessages(true);
    }
  }, [user, isSignedIn, isLoaded, hasInitializedMessages]);

  // Clean markdown formatting from AI responses
  const cleanMarkdown = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove **bold**
      .replace(/\*(.*?)\*/g, '$1')     // Remove *italic*
      .replace(/__(.*?)__/g, '$1')     // Remove __underline__
      .replace(/`(.*?)`/g, '$1')       // Remove `code`
      .replace(/#{1,6}\s/g, '')        // Remove # headers
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // Remove [links](url)
      .replace(/^\s*[-*+]\s/gm, '• ')  // Convert markdown lists to bullets
      .trim();
  };

  // Enhanced typing effect with auto-focus after completion
  const typeMessage = (text, callback) => {
    setIsTyping(true);
    setTypingText('');
    let i = 0;
    const cleanText = cleanMarkdown(text);
    
    const typeInterval = setInterval(() => {
      if (i < cleanText.length) {
        setTypingText(cleanText.slice(0, i + 1));
        i++;
        // Scroll more frequently for better UX
        if (i % 3 === 0) {
          setTimeout(scrollToBottom, 5);
        }
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        setTypingText('');
        callback(cleanText);
        // Final scroll after typing completes
        setTimeout(scrollToBottom, 50);
        
        // Auto-focus input field when AI finishes typing
        setTimeout(() => {
          if (inputRef.current && (isOpen || isFullPage)) {
            inputRef.current.focus();
          }
        }, 100);
      }
    }, 25); // Slightly faster typing
  };

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || busy || isLoading) return;

    const userMessage = {
      type: 'user',
      content: messageText,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setBusy(true);
    setShowSuggestions(false);
    setConnectionStatus('connecting');

    const newHistory = [
      ...conversationHistory,
      { role: 'user', content: messageText }
    ];

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      // Enhanced user context for API - Updated for Clerk
      const userContext = isSignedIn && user ? {
        id: user.id,
        name: getUserDisplayName(),
        email: user.primaryEmailAddress?.emailAddress,
        authenticated: true
      } : {
        authenticated: false
      };

      // Get Clerk token if user is signed in
      const token = isSignedIn && user ? await user.getToken() : null;

      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add auth headers if available
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: messageText,
          conversationHistory: newHistory,
          user: userContext
        }),
      });

      const data = await response.json();
      setConnectionStatus('connected');

      if (data.success) {
        setIsLoading(false);
        typeMessage(data.message, (cleanedMessage) => {
          const aiMessage = {
            type: 'ai',
            content: cleanedMessage,
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, aiMessage]);
          setConversationHistory([
            ...newHistory,
            { role: 'assistant', content: cleanedMessage }
          ]);
        });
      } else {
        throw new Error(data.message || 'Failed to get AI response');
      }
    } catch (error) {
      setConnectionStatus('error');
      setIsLoading(false);
      const errorMessage = {
        type: 'ai',
        content: "I'm having trouble connecting right now, but I'm still here to help! Try asking me about our debate programs, upcoming events, or how to enroll.",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setBusy(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'bg-green-500';
      case 'connecting': return 'bg-yellow-500 animate-pulse';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const toggleFullPage = () => {
    setIsFullPage(!isFullPage);
  };

  // Get display name for UI
  const displayName = getUserDisplayName();

  // Full page modal
  if (isFullPage) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Enhanced Full page header */}
        <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-700 text-white p-6 flex items-center justify-between border-b shadow-lg">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
              <span className="text-white text-sm font-bold">AI</span>
            </div>
            <div>
              <h1 className="font-bold text-xl">ACD AI Assistant</h1>
              <p className="text-sm text-emerald-100">
                {displayName ? `Welcome back, ${displayName}!` : 'African Child Debaters Champions'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="px-4 py-2 bg-green-400/20 backdrop-blur-sm rounded-full text-sm text-green-100 border border-green-400/30">
              <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              Online
            </div>
            <button
              onClick={toggleFullPage}
              className="px-5 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg text-sm transition-all duration-200 border border-white/20"
            >
              Minimize
            </button>
            <button
              onClick={() => {
                setIsFullPage(false);
                setIsOpen(false);
              }}
              className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20"
            >
              <span className="text-white text-lg">×</span>
            </button>
          </div>
        </div>

        {/* Enhanced Full page messages with better spacing */}
        <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-br from-slate-50 via-gray-50 to-emerald-50">
          <div className="max-w-4xl mx-auto space-y-8">
            {messages.map((message, index) => (
              <motion.div
                key={`${message.timestamp}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-2xl px-6 py-5 rounded-2xl shadow-lg ${
                  message.type === 'user'
                    ? 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white'
                    : 'bg-white text-gray-800 border border-gray-200 shadow-md'
                }`}>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  <p className={`text-xs mt-3 ${
                    message.type === 'user' ? 'text-emerald-100' : 'text-gray-500'
                  }`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </motion.div>
            ))}

            {/* Enhanced Typing indicator with better spacing */}
            {isTyping && (
              <motion.div 
                className="flex justify-start"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white border border-gray-200 rounded-2xl px-6 py-5 shadow-md max-w-[85%] min-h-[4rem]">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {typingText}<span className="animate-pulse text-emerald-600">|</span>
                  </p>
                </div>
              </motion.div>
            )}

            {/* Enhanced Loading indicator */}
            {isLoading && !isTyping && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-md">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Enhanced Full page input with better spacing */}
        <div className="p-8 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-4xl mx-auto flex space-x-4">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Ask me about ${displayName ? 'your ACD journey' : 'ACD programs'}...`}
              className="flex-1 px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm transition-all duration-200"
              disabled={isLoading}
              autoFocus
            />
            <button
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading || busy || connectionStatus === 'error'}
              className="px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-gray-300 disabled:to-gray-400 text-white rounded-xl transition-all duration-200 shadow-lg"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Enhanced Compact Chat Widget */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-16 right-0 w-80 h-96 bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Enhanced Compact Header with better spacing */}
            <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-700 text-white px-4 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30">
                  <span className="text-white text-xs font-bold">AI</span>
                </div>
                <div>
                  <span className="text-sm font-semibold">ACD AI Assistant</span>
                  <div className="flex items-center space-x-1 mt-0.5">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                    <p className="text-xs text-emerald-100">
                      {connectionStatus === 'connected' ? 'Online' : 
                       connectionStatus === 'connecting' ? 'Connecting...' : 'Offline'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleFullPage}
                  className="w-7 h-7 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20"
                  title="Expand"
                >
                  <span className="text-xs">↗</span>
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-7 h-7 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20"
                >
                  <span className="text-sm">×</span>
                </button>
              </div>
            </div>

            {/* Enhanced Messages Area with better spacing */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-slate-50 to-emerald-50">
              {messages.map((message, index) => (
                <motion.div
                  key={`${message.timestamp}-${index}`}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] px-4 py-3 rounded-2xl shadow-md ${
                    message.type === 'user'
                      ? 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}>
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-2 ${
                      message.type === 'user' ? 'text-emerald-100' : 'text-gray-500'
                    }`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </motion.div>
              ))}

              {/* Enhanced Typing indicator for compact view */}
              {isTyping && (
                <motion.div 
                  className="flex justify-start"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 max-w-[85%] shadow-md min-h-[2.5rem]">
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">
                      {typingText}<span className="animate-pulse text-emerald-600">|</span>
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Enhanced Loading indicator for compact view */}
              {isLoading && !isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-md">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Enhanced Suggestions with better spacing */}
              {showSuggestions && messages.length === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3 mt-4"
                >
                  <div className="text-gray-600 text-xs font-medium px-1">
                    <span>💡 Try asking:</span>
                  </div>
                  <div className="space-y-2">
                    {suggestions.slice(0, 3).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="text-left text-xs bg-white hover:bg-emerald-50 text-gray-700 px-4 py-3 transition-all duration-200 border border-emerald-200 hover:border-emerald-300 w-full rounded-2xl shadow-sm hover:shadow-md"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} className="h-2" />
            </div>

            {/* Enhanced Input Area with better spacing */}
            <div className="p-4 bg-white/80 backdrop-blur-md border-t border-emerald-100">
              <div className="flex space-x-3 mb-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Ask me about ${displayName ? 'your ACD journey' : 'ACD programs'}...`}
                  className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm transition-all duration-200"
                  disabled={isLoading || busy || connectionStatus === 'error'}
                  autoFocus
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!inputMessage.trim() || isLoading || busy || connectionStatus === 'error'}
                  className="w-11 h-11 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-gray-300 disabled:to-gray-400 text-white flex items-center justify-center transition-all duration-200 rounded-xl shadow-md"
                >
                  <span className="text-sm">→</span>
                </button>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>Powered by Llama</span>
                <div className="flex items-center space-x-2">
                  {displayName && (
                    <span className="text-emerald-600 font-medium">Hi, {displayName}!</span>
                  )}
                  <span className="px-2 py-1 bg-green-100 text-green-700 border border-green-200 rounded-full text-xs font-medium">
                    <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                    Online
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="w-16 h-16 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-600 hover:via-emerald-700 hover:to-teal-700 text-white rounded-lg shadow-xl flex items-center justify-center transition-all duration-300 border-2 border-emerald-400/20"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              className="text-xl font-bold"
            >
              ↓
            </motion.span>
          ) : (
            <motion.div
              key="ai"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex flex-col items-center"
            >
              <span className="text-sm font-bold">AI</span>
              <div className="flex space-x-0.5 mt-0.5">
                <div className="w-1 h-1 bg-white rounded-full animate-pulse"></div>
                <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default AcdAiWidget;